// Stock → Items (spec/13 §6.4): the stock list. The headline figures first — what the stock is worth (with
// `inventory:reports`), how many items are low, out or need a recount, and when the last full count was — then a
// toolbar — search by name, SKU or barcode (a scanner's Enter opens the item it names), the state and kind filters,
// archived items on request — and the table: each item's on hand in its natural unit, its par level, its state in
// words, its average cost and value, and its last movement. A row's name opens the item; its menu holds the actions.

import type { StockItem, StockItemKind, StockLevel, StockStatus } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, Checkbox, Select, SegmentedControl, StatCard, Table, type MenuEntry, type SortDirection, type TableColumn } from '@clavian/ui-web';
import { useState, type KeyboardEvent, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon } from '../../shell/nav-icons';
import { useCurrentVenue } from '../../shell/scope';
import { Forbidden, RowActions, SearchField, SummaryStrip, TableSkeleton, Toolbar, ToolbarSpring } from '../../ui';
import { TickingNumber } from '../../ui/ticking';
import type { StockAccess } from './access';
import { NO_FIGURE, useStockFormat } from './format';
import { LevelBadge } from './parts';
import { useLookupBarcode, useStockItems } from './queries';
import { StockEmpty, useWhen, type StockDialog, type StockLevelFilter, type StockSearch } from './StockPage';

/** Worst first: what needs someone sorts to the top. */
const LEVEL_ORDER: Readonly<Record<StockLevel, number>> = { negative: 0, out: 1, low: 2, ok: 3 };
const KINDS: readonly StockItemKind[] = ['ingredient', 'retail', 'supply'];

const matchesLevel = (filter: StockLevelFilter | undefined, level: StockLevel): boolean =>
  filter === undefined || (filter === 'attention' ? level !== 'ok' : level === filter);

export function StockItemsTab({
  status,
  access,
  search,
  setSearch,
  openDialog,
}: {
  readonly status: StockStatus;
  readonly access: StockAccess;
  readonly search: StockSearch;
  readonly setSearch: (patch: Partial<StockSearch>) => void;
  readonly openDialog: (dialog: StockDialog) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const venue = useCurrentVenue();
  const format = useVenueFormatter();
  const stock = useStockFormat();
  const when = useWhen();
  const [showArchived, setShowArchived] = useState(false);
  const items = useStockItems(venue.organizationId, venue.id, { includeArchived: showArchived });
  const lookup = useLookupBarcode(venue.organizationId, venue.id);
  const [query, setQuery] = useState('');
  const [scanMessage, setScanMessage] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: string; direction: SortDirection }>({ key: 'level', direction: 'ascending' });

  const openItem = (item: StockItem) => setSearch({ item: item.id });

  // A scanner types the digits and Enter: the server knows a pack's barcode and a retail product's too.
  const onSearchKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    const text = query.trim();
    if (!/^\d{8,14}$/.test(text)) return;
    event.preventDefault();
    lookup.mutate(text, {
      onSuccess: (found) => {
        setQuery('');
        setScanMessage(null);
        setSearch({ item: found.item.id });
      },
      onError: (error) => setScanMessage(problemCode(error) === PROBLEM.notFound ? t('stock.picker.unknownBarcode', { barcode: text }) : problem.text(error, { fallback: t('stock.picker.lookupFailed') })),
    });
  };

  const whole = (value: number) => format.number(value);
  // The summary strip (ADR-0043), as on the Floor: one surface split by hairlines, each figure ticking to a new value.
  // What the stock is worth leads on the ember rail; low takes the warning rail, out or below zero the one filled cell.
  const figures = (
    <SummaryStrip label={t('stock.kpi.label')}>
      {access.reports ? <StockValueCard items={items.data} /> : null}
      <StatCard label={t('stock.kpi.low')} value={<TickingNumber className="console-num" value={status.lowCount} format={whole} />} hint={t('stock.kpi.lowHint')} tone={status.lowCount > 0 ? 'warn' : 'default'} />
      <StatCard
        label={t('stock.kpi.attention')}
        value={<TickingNumber className="console-num" value={status.outCount + status.negativeCount} format={whole} />}
        hint={t('stock.kpi.attentionHint', { out: status.outCount, negative: status.negativeCount })}
        tone={status.outCount + status.negativeCount > 0 ? 'danger' : 'default'}
      />
      <StatCard label={t('stock.kpi.soldOut')} value={<TickingNumber className="console-num" value={status.soldOutCount} format={whole} />} hint={status.autoUnavailableOnZeroStock ? t('stock.kpi.soldOutHint') : t('stock.kpi.soldOutOff')} />
      <StatCard
        label={t('stock.kpi.lastCount')}
        value={<span className="console-num">{status.lastFullCountAt === null ? NO_FIGURE : format.custom(status.lastFullCountAt, { day: 'numeric', month: 'short' })}</span>}
        hint={status.lastFullCountAt === null ? t('stock.kpi.neverCounted') : t('stock.kpi.lastCountHint')}
      />
    </SummaryStrip>
  );

  if (items.data === undefined) {
    if (items.isError && problemCode(items.error) === PROBLEM.forbidden) return <Forbidden message={t('stock.notPermitted')} />;
    return (
      <div className="console-stock-tab">
        {figures}
        {items.isError ? <LoadFailure error={items.error} message={t('stock.unavailable')} onRetry={() => void items.refetch()} /> : <TableSkeleton rows={8} label={t('stock.loading')} />}
      </div>
    );
  }

  const all = items.data;
  if (all.length === 0 && !showArchived) {
    return (
      <div className="console-stock-tab">
        {figures}
        <StockEmpty
          title={t('stock.items.emptyTitle')}
          description={t('stock.items.empty')}
          action={
            access.items || access.manage ? (
              <div className="console-form__actions">
                {access.items ? (
                  <Button variant="primary" icon={<Icon name="plus" />} onClick={() => openDialog({ kind: 'newItem' })}>
                    {t('stock.items.new')}
                  </Button>
                ) : null}
                {access.manage ? (
                  <Button variant="secondary" onClick={() => openDialog({ kind: 'copy' })}>
                    {t('stock.copy.action')}
                  </Button>
                ) : null}
              </div>
            ) : undefined
          }
        />
      </div>
    );
  }

  const needle = query.trim().toLowerCase();
  const byKind = all.filter((item) => search.kind === undefined || item.kind === search.kind);
  const count = (filter: StockLevelFilter | undefined) => byKind.filter((item) => matchesLevel(filter, item.level)).length;
  const rows = byKind
    .filter((item) => matchesLevel(search.level, item.level))
    .filter((item) => needle === '' || [item.name, item.sku ?? '', item.barcode ?? '', ...item.packs.map((pack) => pack.barcode ?? '')].some((text) => text.toLowerCase().includes(needle)))
    .sort((a, b) => {
      const direction = sort.direction === 'ascending' ? 1 : -1;
      if (sort.key === 'value') return direction * ((a.value?.amount ?? 0) - (b.value?.amount ?? 0)) || a.name.localeCompare(b.name);
      if (sort.key === 'moved') return direction * (a.lastMovementAt ?? '').localeCompare(b.lastMovementAt ?? '') || a.name.localeCompare(b.name);
      if (sort.key === 'level') return direction * (LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level]) || a.name.localeCompare(b.name);
      return direction * a.name.localeCompare(b.name);
    });

  const actionsFor = (item: StockItem): MenuEntry[] => {
    const supply = item.kind === 'supply';
    const entries: MenuEntry[] = [{ id: 'open', label: t('stock.items.open'), onSelect: () => openItem(item) }];
    if (!item.archived) {
      if (access.receive && (!access.receiveSuppliesOnly || supply)) entries.push({ id: 'receive', label: t('stock.actions.receive'), onSelect: () => openDialog({ kind: 'receive', item }) });
      if (access.usage && (!access.usageSuppliesOnly || supply)) entries.push({ id: 'usage', label: t('stock.actions.usage'), onSelect: () => openDialog({ kind: 'usage', item, mode: supply ? 'issue' : 'waste' }) });
      if (access.count && (!access.countSuppliesOnly || supply)) entries.push({ id: 'count', label: t('stock.items.countThis'), onSelect: () => openDialog({ kind: 'count', item }) });
      if (access.adjust && (!access.adjustSuppliesOnly || supply)) entries.push({ id: 'adjust', label: t('stock.items.adjust'), onSelect: () => openDialog({ kind: 'usage', item, mode: 'adjust' }) });
    }
    if (access.items && (!access.manageSuppliesOnly || supply)) entries.push({ id: 'edit', label: t('stock.items.edit'), onSelect: () => openDialog({ kind: 'editItem', item }) });
    return entries;
  };

  const columns: TableColumn<StockItem>[] = [
    {
      key: 'name',
      header: t('stock.column.item'),
      sortable: true,
      cell: (item) => (
        <button type="button" className="console-cell-link" onClick={() => openItem(item)}>
          <span className="console-stock-row__name">{item.name}</span>
          <span className="console-cell-sub">
            {[t(`stock.kind.${item.kind}`), item.archived ? t('stock.items.archived') : null, item.sku].filter((part): part is string => part !== null && part !== '').join(' · ')}
          </span>
        </button>
      ),
    },
    {
      key: 'onHand',
      header: t('stock.column.onHand'),
      numeric: true,
      cell: (item) => <span className={item.onHand < 0 ? 'console-num console-stock-row__negative' : 'console-num console-stock-row__onhand'}>{stock.quantity(item.onHand, item)}</span>,
    },
    { key: 'level', header: t('stock.column.status'), sortable: true, cell: (item) => <LevelBadge level={item.level} /> },
    { key: 'par', header: t('stock.column.par'), numeric: true, cell: (item) => <span className="console-num console-muted">{item.parLevel === null ? NO_FIGURE : stock.quantity(item.parLevel, item)}</span> },
    ...(access.reports
      ? [
          { key: 'cost', header: t('stock.column.averageCost'), numeric: true, cell: (item: StockItem) => <span className="console-num console-muted">{stock.unitCost(item.averageUnitCost, item)}</span> },
          { key: 'value', header: t('stock.column.value'), numeric: true, sortable: true, cell: (item: StockItem) => <span className="console-money">{stock.money(item.value)}</span> },
        ]
      : []),
    {
      key: 'moved',
      header: t('stock.column.lastMovement'),
      sortable: true,
      cell: (item) => <span className="console-num console-muted">{item.lastMovementAt === null ? t('stock.items.neverMoved') : when(item.lastMovementAt)}</span>,
    },
    { key: 'actions', header: <span className="clav-visually-hidden">{t('common.actions')}</span>, cell: (item) => <RowActions name={item.name} items={actionsFor(item)} /> },
  ];

  return (
    <div className="console-stock-tab">
      {figures}
      <Toolbar label={t('stock.items.toolbar')} className="console-stock-toolbar">
        <SearchField
          label={t('stock.items.search')}
          placeholder={t('stock.items.searchPlaceholder')}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setScanMessage(null);
          }}
          onKeyDown={onSearchKey}
        />
        <SegmentedControl<StockLevelFilter | 'all'>
          label={t('stock.filter.level')}
          size="sm"
          value={search.level ?? 'all'}
          onValueChange={(value) => setSearch({ level: value === 'all' ? undefined : value })}
          options={[
            { value: 'all', label: t('stock.filter.all'), count: count(undefined) },
            { value: 'low', label: t('stock.filter.low'), count: count('low') },
            { value: 'out', label: t('stock.filter.out'), count: count('out') },
            { value: 'negative', label: t('stock.filter.negative'), count: count('negative') },
          ]}
        />
        <div className="console-stock-toolbar__kind">
          <Select
            aria-label={t('stock.filter.kind')}
            value={search.kind ?? 'all'}
            onValueChange={(value) => setSearch({ kind: value === 'all' ? undefined : (value as StockItemKind) })}
            options={[{ value: 'all', label: t('stock.filter.allKinds') }, ...KINDS.map((kind) => ({ value: kind, label: t(`stock.kindPlural.${kind}`) }))]}
          />
        </div>
        <Checkbox label={t('stock.filter.archived')} checked={showArchived} onCheckedChange={(checked) => setShowArchived(checked === true)} />
        <ToolbarSpring />
        {access.manage ? (
          <RowActions name={t('stock.items.listName')} items={[{ id: 'copy', label: t('stock.copy.action'), onSelect: () => openDialog({ kind: 'copy' }) }]} />
        ) : null}
        {access.items ? (
          <Button variant="secondary" size="sm" icon={<Icon name="plus" />} onClick={() => openDialog({ kind: 'newItem' })}>
            {t('stock.items.new')}
          </Button>
        ) : null}
      </Toolbar>
      {scanMessage !== null ? (
        <p className="console-stock-pick__message" role="status">
          {scanMessage}
        </p>
      ) : null}
      <div className="console-table-card console-stock-table">
        <Table
          caption={t('stock.items.caption')}
          captionHidden
          columns={columns}
          rows={rows}
          rowKey={(item) => item.id}
          sort={sort}
          onSortChange={(key, direction) => setSort({ key, direction })}
          empty={needle === '' ? t('stock.items.filterEmpty') : t('common.noMatches', { query: query.trim() })}
        />
      </div>
    </div>
  );
}

/** What the stock on hand is worth: the sum of the items' values, each exact; ₹ figures need `inventory:reports`. */
function StockValueCard({ items }: { readonly items: readonly StockItem[] | undefined }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const venue = useCurrentVenue();
  const currency = venue.currency ?? 'INR';
  const live = (items ?? []).filter((item) => !item.archived);
  const total = live.reduce((sum, item) => sum + (item.value?.amount ?? 0), 0);
  const uncosted = live.filter((item) => item.averageUnitCost === null && item.onHand !== 0).length;
  return (
    <StatCard
      className="console-summary__cell--live"
      label={t('stock.kpi.value')}
      value={items === undefined ? <span className="console-num">{NO_FIGURE}</span> : <TickingNumber className="console-num" value={total} format={(amount) => format.money({ amount, currency }, 'noFraction')} />}
      hint={uncosted > 0 ? t('stock.kpi.valueUncosted', { count: uncosted }) : t('stock.kpi.valueHint')}
    />
  );
}
